import type { LinkedIssue, TaskSnapshot } from '../../../stores/types';
import { isOpenCriticalIssue } from '../../../utils/linkedIssueHelpers';

export const UNCATEGORIZED_LABEL = 'Uncategorized';

export type IssueCountBucket = {
  label: string;
  count: number;
};

export type OpenCriticalIssueRow = LinkedIssue & {
  /** Tasks that reference this ticket (names, truncated for display elsewhere). */
  taskNames: string[];
};

export type OpenCriticalIssuesAnalytics = {
  issues: OpenCriticalIssueRow[];
  total: number;
  byCategory: IssueCountBucket[];
};

function resolveTaskLabel(task: TaskSnapshot): string {
  const name = String(task.taskName ?? '').trim();
  return name || `Task ${task.id}`;
}

function toSortedBuckets(counts: Map<string, number>): IssueCountBucket[] {
  return Array.from(counts.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

/**
 * Unique open Critical issues across dashboard tasks, plus category aggregates.
 */
export function deriveOpenCriticalIssues(tasks: TaskSnapshot[]): OpenCriticalIssuesAnalytics {
  const byTicket = new Map<string, OpenCriticalIssueRow>();

  for (const task of tasks) {
    const linked = Array.isArray(task.linkedIssues) ? task.linkedIssues : [];
    if (linked.length === 0) continue;
    const taskLabel = resolveTaskLabel(task);

    for (const issue of linked) {
      if (!isOpenCriticalIssue(issue)) continue;
      const existing = byTicket.get(issue.ticketId);
      if (existing) {
        if (!existing.taskNames.includes(taskLabel)) {
          existing.taskNames.push(taskLabel);
        }
        if (issue.currentlyAffected) {
          existing.currentlyAffected = true;
        }
        continue;
      }
      byTicket.set(issue.ticketId, {
        ...issue,
        taskNames: [taskLabel],
      });
    }
  }

  const issues = Array.from(byTicket.values()).sort((a, b) => {
    if (a.currentlyAffected !== b.currentlyAffected) {
      return a.currentlyAffected ? -1 : 1;
    }
    return (a.title || a.ticketId).localeCompare(b.title || b.ticketId);
  });

  const categoryCounts = new Map<string, number>();
  for (const issue of issues) {
    const category = issue.category.trim() || UNCATEGORIZED_LABEL;
    categoryCounts.set(category, (categoryCounts.get(category) ?? 0) + 1);
  }

  return {
    issues,
    total: issues.length,
    byCategory: toSortedBuckets(categoryCounts),
  };
}
